import Button from 'components/Button';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import PageSection from 'components/PageSection';
import { BackgroundExportTypes } from 'further-types/background-exports';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import { useGetRole } from 'hooks/ui/useGetRole';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import FullPageSuccess from 'components/FullPageSuccess';
import { FieldRow } from 'components/FormElements';
import { FirmSelector } from 'components/FirmSelector';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import ButtonGroup from 'components/ButtonGroup';

type FormValues = {
  firmId: string | null;
  params: {
    dateRange?: {
      startDate?: Date;
      endDate?: Date;
    };
  };
};

const PortalUsageDataReport: FC = () => {
  const { isSuperAdmin, firmId } = useGetRole();
  const { handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      params: {
        dateRange: {
          startDate: undefined,
          endDate: undefined,
        },
      },
      firmId,
    },
  });
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);

  const breadcrumbs = [
    { label: 'Dashboard', link: '/' },
    { label: 'Export portal usage data', link: '/', isActive: true },
  ];

  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleExport = async (data: FormValues) => {
    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.PortalUsageDataReport,
      firmId: data.firmId ?? undefined,
      params: data.params,
    });

    setShowSuccessScreen(true);
  };

  const heading = 'System Exports: Portal usage data';

  if (showSuccessScreen) {
    return (
      <PageContainer heading={heading} breadcrumbs={breadcrumbs}>
        <FullPageSuccess
          title="Your portal usage data report is being exported"
          additionalText="Your report will be available soon in the My Downloads section below"
          proceedText="Create another report"
          proceedCallback={() => setShowSuccessScreen(false)}
        />
      </PageContainer>
    );
  }

  return (
    <>
      <PageContainer heading={heading} breadcrumbs={breadcrumbs}>
        <PageContent>
          <form onSubmit={handleSubmit(handleExport)}>
            <PageSection title="Export portal usage data report" withSpacing>
              {isSuperAdmin && (
                <FieldRow centerTitle title="Firm">
                  <FirmSelector
                    firmId={watch('firmId') ?? undefined}
                    onChange={(firmId) => setValue('firmId', firmId)}
                    required={false}
                  />
                </FieldRow>
              )}

              <FieldRow centerTitle title="Date range (optional)">
                <MuiDateRangePicker
                  dateRange={watch('params.dateRange')}
                  onDateRangeChange={(dates) => {
                    setValue('params.dateRange', dates);
                  }}
                  placeholder="Select date range"
                />
              </FieldRow>

              <ButtonGroup>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isExporting}
                >
                  Export
                </Button>
              </ButtonGroup>
            </PageSection>
          </form>
        </PageContent>
      </PageContainer>
    </>
  );
};

export default PortalUsageDataReport;
